import { Flex } from 'front-end/src/components'
import { IconCheckFull } from 'front-end/src/components/common/icon/IconCheckFull'
import { IconCircleEmpty } from 'front-end/src/components/common/icon/IconCircleEmpty'
import { IconCircleFull } from 'front-end/src/components/common/icon/IconCircleFull'
import { IconXFull } from 'front-end/src/components/common/icon/IconXFull'
import {
  Status,
  StatusClosedGrey,
  StatusDraftGrey,
  StatusInProgressGrey,
  StatusOpenGrey
} from 'front-end/src/components/common/status/Status'
import {
  EventModel,
  RecurringEventModelAsStudent
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import React, { FC } from 'react'
import { brandColors } from 'shared/basicPalette'
import dayjs from 'shared/dayjs'

import { BoxProps, FlexProps } from '@chakra-ui/react'
import { t } from '@lingui/macro'

import { CalendarEventStatusType } from '../../../../../types/CalendarEvent'
import { getUTCDateFromTimeZoneDate } from '../../event-edit/tabs/event-detail/eventDetailUtils'
import { isIE } from 'front-end/../shared/helpers'

export type StatusOfEventProps = Pick<
  RecurringEventModelAsStudent,
  'published_date' | 'start_time' | 'end_time' | 'timezone'
> & {
  events?: EventModel[]
}

export enum StatusEventStatusType {
  COMPLETED = 'completed',
  DRAFT = 'draft',
  OPEN = 'open',
  INPROGRESS = 'in-progress'
}

function getLastEventFromSeries(events: EventModel[]): EventModel {
  return events.reduce(
    (a, b) =>
      // @ts-ignore
      new Date(a.end_time) > new Date(b.end_time) ? a : b,
    []
  )
}

// Event start time and end time at event edit page is converted on UTC dayjs object without timezone
// we need to convert it back to UTC to find correct status
export function getStatus(event: StatusOfEventProps): CalendarEventStatusType {
  const { published_date, start_time, end_time, timezone } = event

  if (!published_date) return StatusEventStatusType.DRAFT

  const now = dayjs.utc()

  if (event?.events?.length) {
    // For recurring events
    const lastEvent = getLastEventFromSeries(event.events)
    const endTimeString = dayjs.isDayjs(lastEvent.end_time)
      ? getUTCDateFromTimeZoneDate({
          date: lastEvent.end_time,
          timezone: !isIE ? timezone || dayjs.tz.guess() : 'UTC'
        })
      : lastEvent.end_time
    const endDayjs = dayjs(endTimeString)
    if (endDayjs.isBefore(now)) return StatusEventStatusType.COMPLETED

    return StatusEventStatusType.OPEN
  } else {
    // For ordinary events
    const endTimeString = dayjs.isDayjs(end_time)
      ? getUTCDateFromTimeZoneDate({
          date: end_time,
          timezone: !isIE ? timezone || dayjs.tz.guess() : 'UTC'
        })
      : end_time
    const endDayjs = dayjs(endTimeString)
    if (endDayjs.isBefore(now)) return StatusEventStatusType.COMPLETED

    const startTimeString = dayjs.isDayjs(start_time)
      ? getUTCDateFromTimeZoneDate({
          date: start_time,
          timezone: !isIE ? timezone || dayjs.tz.guess() : 'UTC'
        })
      : start_time
    const startDayjs = dayjs(startTimeString)
    if (startDayjs.isBefore(now)) return StatusEventStatusType.INPROGRESS

    return StatusEventStatusType.OPEN
  }
}

const StatusOfEvent: FC<
  BoxProps & {
    event: StatusOfEventProps
  }
> = (props) => {
  const { event, ...rest } = props
  const status = getStatus(event)

  return (
    <Flex data-test="EventStatus" {...rest}>
      {
        {
          draft: <StatusDraftGrey />,
          completed: <StatusClosedGrey />,
          open: <StatusOpenGrey />,
          'in-progress': <StatusInProgressGrey />
        }[status]
      }
    </Flex>
  )
}

export const StatusRSVPNoResponse: FC<FlexProps> = (props) => (
  <Status
    text={t`No response`}
    variant="white"
    icon={<IconCircleEmpty size="sm" />}
    {...props}
  />
)

export const StatusRSVPDeclined: FC<FlexProps> = (props) => (
  <Status
    text={t`Declined`}
    variant="white"
    borderColor={brandColors.red.mid}
    color={brandColors.red.mid}
    icon={<IconCircleFull size="sm" />}
    {...props}
  />
)

export const StatusRSVPAccepted: FC<FlexProps> = (props) => (
  <Status
    text={t`Accepted`}
    variant="white"
    borderColor={brandColors.green.mid}
    color={brandColors.green.mid}
    icon={<IconCircleFull size="sm" />}
    {...props}
  />
)

export const StatusUserAttended: FC<FlexProps> = (props) => (
  <Status
    text={t`Attended`}
    variant="white"
    borderColor={brandColors.green.mid}
    color={brandColors.green.mid}
    icon={<IconCheckFull size="sm" />}
    {...props}
  />
)

export const StatusUserDidNotAttend: FC<FlexProps> = (props) => (
  <Status
    text={t`Did not attend`}
    variant="white"
    borderColor={brandColors.red.mid}
    color={brandColors.red.mid}
    icon={<IconXFull size="sm" />}
    {...props}
  />
)

export default StatusOfEvent
